import React from 'react';
import {
  SkeletonActionsEnd,
  SkeletonBlock,
  SkeletonList,
  SkeletonNameStack,
  SkeletonRow,
} from './ProcessesStates.styled';

// Stable identifiers for each skeleton row. The list is purely decorative
// (placeholders that get replaced wholesale once the real data loads), but
// we use named keys instead of array indices to keep eslint/Sonar quiet
// about `react/no-array-index-key`.
const SKELETON_ROW_IDS = [
  'skel-row-1',
  'skel-row-2',
  'skel-row-3',
  'skel-row-4',
  'skel-row-5',
  'skel-row-6',
] as const;

export interface ProcessesLoadingProps {
  dataTestId?: string;
}

/**
 * Skeleton state for the process list. Mirrors the live `ProcessesList`
 * card layout so the visual jump between loading → loaded is minimal:
 *   [40x40 icon block] [title line + meta line] [status pill + Open btn + kebab]
 */
const ProcessesLoading: React.FC<ProcessesLoadingProps> = ({
  dataTestId = 'processes-loading',
}) => (
  <SkeletonList
    role="status"
    aria-live="polite"
    aria-label="Loading processes"
    data-testid={dataTestId}
  >
    {SKELETON_ROW_IDS.map((rowId, i) => (
      <SkeletonRow key={rowId} data-testid={`${dataTestId}__row-${i}`}>
        <SkeletonBlock $width="40px" $height="40px" $radius="8px" />
        <SkeletonNameStack>
          <SkeletonBlock $width="40%" $height="14px" />
          <SkeletonBlock $width="60%" $height="11px" />
        </SkeletonNameStack>
        <SkeletonActionsEnd>
          <SkeletonBlock $width="80px" $height="20px" $radius="999px" />
          <SkeletonBlock $width="60px" $height="28px" $radius="999px" />
          <SkeletonBlock $width="28px" $height="28px" $radius="50%" />
        </SkeletonActionsEnd>
      </SkeletonRow>
    ))}
  </SkeletonList>
);

export default ProcessesLoading;
